import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import EmptyState from "@/components/base/EmptyState";
import DownloadModal from "@/components/base/DownloadModal";
import Toast from "@/components/base/Toast";
import usePageMeta from "@/hooks/usePageMeta";
import { getMovieById, movies, watchApps } from "@/mocks/movies";
import type { WatchApp } from "@/mocks/movies";
import DetailHero from "./components/DetailHero";
import AppRecommend from "./components/AppRecommend";
import RelatedMovies from "./components/RelatedMovies";

export default function Detail() {
  const params = useParams<{ id: string }>();
  const movie = getMovieById(params.id ?? "");
  const [downloadApp, setDownloadApp] = useState<WatchApp | null>(null);
  const [toast, setToast] = useState("");

  usePageMeta({
    title: movie ? `《${movie.title}》详情 - 看片免费app网站` : "影视资源不存在 - 看片免费app网站",
    description: movie
      ? `${movie.title}（${movie.year}·${movie.region}）：${movie.synopsis.slice(0, 70)}… 查看影片详情、评分与适配看片APP推荐。`
      : "该影视资源可能已下架或链接有误，可返回首页继续浏览其他影视内容。",
    keywords: movie
      ? `${movie.title},${movie.title}详情,${movie.genres.join(",")},${movie.type},看片app`
      : "影视资源,看片免费app网站",
  });

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((item) => item.id !== movie.id)
      .map((item) => {
        const sharedGenres = item.genres.filter((genre) => movie.genres.includes(genre)).length;
        const typeBonus = item.type === movie.type ? 2 : 0;
        return { item, score: sharedGenres * 2 + typeBonus + item.rating / 10 };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((entry) => entry.item);
  }, [movie]);

  const recommendedApps = useMemo(() => {
    const index = Number(movie?.id.slice(-1) ?? "1") % 5;
    return watchApps.slice(index, index + 3);
  }, [movie]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制到剪贴板，可以分享给朋友了。");
    } catch {
      const input = document.createElement("textarea");
      input.value = url;
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setToast("链接已复制到剪贴板，可以分享给朋友了。");
    }
  };

  if (!movie) {
    return (
      <SiteLayout>
        <div className="w-full max-w-page mx-auto px-4 md:px-8 py-10">
          <EmptyState
            icon="ri-film-line"
            title="影视资源不存在或已下架"
            description="这部作品的资料可能已经调整或暂时下架。你可以返回首页看看今日推荐，或者搜索其他片名。"
            primaryLabel="返回首页"
            secondaryLabel="去搜索其它影片"
            secondaryTo="/search"
          />
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            {movies.slice(0, 4).map((item) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="px-4 py-2 rounded-md border border-background-300 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors"
              >
                {item.title}
              </Link>
            ))}
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <DetailHero movie={movie} onShare={handleShare} />

      <div className="w-full max-w-page mx-auto px-4 md:px-8 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <section className="lg:col-span-8">
            <h2 className="text-lg md:text-xl font-bold text-foreground-900">剧情简介</h2>
            <div className="mt-3 rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <p className="text-sm md:text-[15px] leading-7 text-foreground-700">{movie.synopsis}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${movie.type}`}
                    className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors"
                  >
                    {genre}
                  </Link>
                ))}
                <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-900 text-xs">{movie.region}</span>
                <span className="px-2.5 py-1 rounded-md bg-background-200/70 text-foreground-700 text-xs">
                  {movie.year}
                </span>
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-4 flex items-start gap-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-500 text-primary-950 shrink-0">
                <i className="ri-information-line"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-accent-950">观看说明</p>
                <p className="mt-1 text-xs leading-6 text-accent-900">
                  本站不存储任何视频文件，仅提供影视资料与看片应用介绍。页面中的播放与下载入口均为前端演示，
                  请通过正规渠道观看影片。
                </p>
              </div>
            </div>
          </section>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="text-base font-semibold text-foreground-900">影片信息</h2>
              <dl className="mt-4 space-y-3 text-xs">
                {[
                  { label: "导演", value: movie.director },
                  { label: "主演", value: movie.cast.join(" / ") },
                  { label: "类型", value: movie.genres.join(" / ") },
                  { label: "地区", value: movie.region },
                  { label: "上映年份", value: String(movie.year) },
                  { label: "片长", value: movie.duration },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="flex-1 text-foreground-800 leading-5">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-4 rounded-lg border border-background-200/80 bg-primary-50 p-5">
              <h2 className="text-base font-semibold text-primary-900">热门看片工具</h2>
              <ul className="mt-3 space-y-3">
                {watchApps.slice(0, 4).map((app) => (
                  <li key={app.id}>
                    <Link
                      to={`/app/detail/${app.id}`}
                      className="flex items-center gap-3 group"
                    >
                      <div className="w-9 h-9 rounded-md overflow-hidden bg-primary-900 shrink-0">
                        <img
                          src={app.icon}
                          alt={`${app.name}图标`}
                          title={`${app.name} - 看片应用`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                          {app.name}
                        </p>
                        <p className="text-[11px] text-foreground-500 truncate">{app.category} · {app.size}</p>
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-300"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        <div className="mt-10">
          <AppRecommend apps={recommendedApps} onDownload={setDownloadApp} />
        </div>

        <div className="mt-12">
          <RelatedMovies items={related} type={movie.type} />
        </div>
      </div>

      <DownloadModal
        open={Boolean(downloadApp)}
        appName={downloadApp?.name ?? ""}
        onClose={() => setDownloadApp(null)}
      />
      {toast && <Toast message={toast} onClose={() => setToast("")} />}
    </SiteLayout>
  );
}